import {
  useApolloClient,
  useMutation as useApolloMutation
} from "@apollo/client/react"
import { useTranslation } from "react-i18next"

import { callMutation } from "shared/api/callMutation"
import { evictProjectPrice } from "shared/api/price/evictPriceCache"
import { mapAPITypeToTypename } from "shared/api/serializer/mapApiTypeToTypename"
import { mapTypenameToAPIType } from "shared/api/serializer/mapTypenameToAPIType"
import { MutationSchema, MutationTypename } from "shared/api/types"
import { emitAppEvent } from "shared/lib/appEventEmitter"
import { exists } from "shared/lib/exists"
import { generateUUID } from "shared/lib/generateUUID"
import { useProjectRequestMonitor } from "shared/lib/request-monitoring/useProjectRequestMonitor"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { ApolloFetchPolicy } from "shared/types/ApolloFetchPolicy"
import {
  ProjectParentAPIType,
  ProjectParentTypename,
  RoomTypename
} from "shared/types/Project"
import {
  CREATE_PROJECT_CHILD,
  CREATE_ROOM
} from "shared/types/ProjectMutations"
import {
  CreateRoomInputSchema,
  RoomSchema
} from "shared/types/__generated__/validation"

import { getProjectChildren } from "entities/project-children/lib/getProjectChildren"
import { insertProjectChild } from "entities/project-children/lib/insertProjectChild"
import { useProjectChildrenLazyQuery } from "entities/projects/graphql/queries.generated"
import {
  CreateRoomDocument,
  CreateRoomMutation,
  CreateRoomMutationVariables
} from "entities/rooms/graphql/mutations.generated"
import { useProjectStoreActions } from "features/project-management/model/store/useProjectStoreActions"
import { RoomFormData } from "features/room-management/types/RoomFormData"
import z from "zod"

import { evictRoom } from "../lib/evictRoom"
import { getRoomComplete } from "../lib/getRoomComplete"
import { getRoomNumberFallback } from "../lib/getRoomNumberFallback"

const CreateRoomVariablesSchema = z.object({
  parentID: z.string(),
  parentType: z.string(),
  input: CreateRoomInputSchema()
})

export function useCreateRoom() {
  const { t } = useTranslation()
  const { cache: apolloCache } = useApolloClient()
  const [fetchProject] = useProjectChildrenLazyQuery()
  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      loading: t("Request.Room.CREATE_ROOM.Start"),
      success: t("Request.Room.CREATE_ROOM.Success"),
      failure: t("Request.Room.CREATE_ROOM.Failure")
    }
  })
  const { setElementPending, unsetElementPending } = useProjectStoreActions()
  const startProjectRequest = useProjectRequestMonitor()

  const cleanup = ({
    projectID,
    roomID
  }: {
    projectID: string
    roomID: string
  }) => {
    fetchProject({
      variables: { id: projectID },
      fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
    })
    unsetElementPending({ itemID: roomID, projectID })
  }

  const [apolloMutate, rest] = useApolloMutation<
    CreateRoomMutation,
    CreateRoomMutationVariables
  >(CreateRoomDocument, {
    update: (cache, result, options) => {
      if (result.data?.room && options.variables?.parentID) {
        insertProjectChild({
          child: result.data?.room,
          position: result.data?.room.position,
          parentID: options.variables.parentID,
          parentType: mapAPITypeToTypename(
            options.variables.parentType
          ) as ProjectParentTypename,
          cache
        })
      }
    }
  })

  const createRoom = useEvent(
    async ({
      room: roomData,
      displayPosition,
      projectID,
      parentID,
      parentType
    }: {
      room: RoomFormData["room"]
      displayPosition?: number
      projectID: string
      parentID: string
      parentType: ProjectParentTypename
    }) => {
      const { raiseSuccessToast, raiseErrorToast } = raiseLoadingToast()
      await fetchProject({ variables: { id: projectID } })
      const projectChildren = getProjectChildren(
        parentID,
        parentType,
        apolloCache
      )
      const position = exists(displayPosition)
        ? displayPosition
        : projectChildren?.length || 0
      const id = roomData.id || generateUUID()
      const room = {
        ...roomData,
        number: getRoomNumberFallback(roomData, "create"),
        position,
        id
      }
      const optimisticRoom = getRoomComplete(room)

      const completeProjectRequest = startProjectRequest({
        projectID,
        requestType: CREATE_ROOM
      })
      setElementPending({ itemID: id, projectID })
      emitAppEvent(CREATE_PROJECT_CHILD, {
        id,
        projectID,
        parentID,
        parentType,
        type: RoomTypename
      })

      return callMutation(apolloMutate, {
        variables: {
          parentID,
          parentType: mapTypenameToAPIType(parentType) as ProjectParentAPIType,
          input: room
        },
        transform: data => data.room,
        schemas: {
          input: CreateRoomVariablesSchema,
          response: MutationSchema.extend({ room: RoomSchema() })
        },
        apolloOptions: {
          optimisticResponse: optimisticRoom
            ? { __typename: MutationTypename, room: optimisticRoom }
            : undefined,
          onCompleted: () => {
            completeProjectRequest()
            cleanup({ projectID, roomID: room.id })
            raiseSuccessToast()
          },
          onError: () => {
            completeProjectRequest()
            evictRoom(room.id, apolloCache)
            evictProjectPrice(projectID, apolloCache, true)
            cleanup({ projectID, roomID: room.id })
            raiseErrorToast()
          }
        }
      })
    }
  )
  return [createRoom, rest] as [typeof createRoom, typeof rest]
}
